import { allocate, bpsOf, roundHalfUp } from './round';
import type { CostBreakdown, PricingInput } from './types';

export interface CostResult {
  cost: CostBreakdown;
  perUnit: { grams: number; printSeconds: number };
  totals: { quantity: number; plates: number; grams: number; printSeconds: number };
  machinePerHourCentavos: number;
}

/**
 * Unit production cost (docs/SPEC.md "Unit production cost"):
 *   material = Σ grams × price/kg ÷ 1000 × (1 + waste)
 *   machine/h = W ÷ 1000 × tariff + price ÷ lifetime h + maintenance/h
 *   unit cost = (material + hours × machine/h) × (1 + failure) + labor + supplies + packaging + design share
 * Each row is rounded to the centavo and the unit cost is their sum, so the breakdown always adds up.
 */
/** Machine cost per print hour in centavos, unrounded (depreciation + energy + maintenance). */
export function machineRate(
  printer: PricingInput['printer'],
  energy: PricingInput['energy'],
): number {
  const energyPerHour =
    (printer.powerWatts / 1000) * (energy.tariffPerKwh + energy.bandeiraPerKwh) * 100;
  const depreciationPerHour =
    printer.lifetimeHours > 0 ? printer.purchasePriceCentavos / printer.lifetimeHours : 0;
  return energyPerHour + depreciationPerHour + printer.maintenancePerHourCentavos;
}

export function unitCost(input: PricingInput): CostResult {
  const { printer, energy, rates, labor } = input;
  const quantity = Math.max(1, input.quantity);

  let gramsPerUnit = 0;
  let secondsPerUnit = 0;
  let materialPerUnit = 0;
  let plates = 0;
  let totalGrams = 0;
  let totalSeconds = 0;
  for (const plate of input.plates) {
    const units = Math.max(1, plate.unitsPerPlate);
    const plateGrams = plate.slots.reduce((sum, s) => sum + s.grams, 0);
    const plateMaterial = plate.slots.reduce(
      (sum, s) => sum + (s.grams * s.pricePerKgCentavos) / 1000,
      0,
    );
    gramsPerUnit += plateGrams / units;
    secondsPerUnit += plate.printSeconds / units;
    materialPerUnit += plateMaterial / units;
    const printed = Math.ceil(quantity / units);
    plates += printed;
    totalGrams += printed * plateGrams;
    totalSeconds += printed * plate.printSeconds;
  }

  const materialRaw = materialPerUnit * (1 + rates.wasteBps / 10_000);

  const hours = secondsPerUnit / 3600;
  const energyPerHour =
    (printer.powerWatts / 1000) * (energy.tariffPerKwh + energy.bandeiraPerKwh) * 100;
  const depreciationPerHour =
    printer.lifetimeHours > 0 ? printer.purchasePriceCentavos / printer.lifetimeHours : 0;
  const maintenancePerHour = printer.maintenancePerHourCentavos;
  const machineParts = [
    depreciationPerHour * hours,
    energyPerHour * hours,
    maintenancePerHour * hours,
  ];
  const machineRaw = machineParts.reduce((a, b) => a + b, 0);

  const failuresRaw = bpsOf(materialRaw + machineRaw, rates.failureBps);
  const laborRaw =
    ((labor.finishingSecondsPerUnit + labor.packingSecondsPerUnit) / 3600) *
    labor.ratePerHourCentavos;
  const suppliesRaw = input.supplies.reduce(
    (sum, s) => sum + s.unitCostCentavos * s.quantityPerUnit,
    0,
  );
  const designRaw = input.design
    ? ((input.design.seconds / 3600) * input.design.ratePerHourCentavos) / quantity
    : 0;

  const material = roundHalfUp(materialRaw);
  const machine = roundHalfUp(machineRaw);
  const [depreciation = 0, energyCost = 0, maintenance = 0] = allocate(machine, machineParts);
  const failures = roundHalfUp(failuresRaw);
  const laborCost = roundHalfUp(laborRaw);
  const packaging = roundHalfUp(input.packagingCentavos);
  const supplies = roundHalfUp(suppliesRaw);
  const design = roundHalfUp(designRaw);

  return {
    cost: {
      material,
      machine,
      machineParts: { depreciation, energy: energyCost, maintenance },
      failures,
      labor: laborCost,
      packaging,
      supplies,
      design,
      unitCost: material + machine + failures + laborCost + packaging + supplies + design,
    },
    perUnit: {
      grams: Math.round(gramsPerUnit * 10) / 10,
      printSeconds: Math.round(secondsPerUnit),
    },
    totals: {
      quantity,
      plates,
      grams: Math.round(totalGrams * 10) / 10,
      printSeconds: Math.round(totalSeconds),
    },
    machinePerHourCentavos: roundHalfUp(energyPerHour + depreciationPerHour + maintenancePerHour),
  };
}
